import { loadableReady } from "@loadable/component";
import { createBrowserHistory } from "history";
import i18next from "i18next";
import "lazysizes";
import "lazysizes/plugins/attrchange/ls.attrchange";
import { Provider } from "mobx-react";
import ReactDOM from "react-dom";
import { HelmetProvider } from "react-helmet-async";
import { Route, Router } from "react-router-dom";
import i18n from "@inferno/renderer-shared-i18n";

import { App } from "../app/core/components/App.component";
import { AdobeManager } from "../app/core/lib/adobe";
import { adsHelper } from "../app/core/lib/ads";
import { getSessionBackend } from "../app/core/lib/auth/backends";
import { setBeacon } from "../app/core/lib/beacon";
import { ComScoreManager } from "../app/core/lib/comScore";
import { initFbPixel, initFbSDK } from "../app/core/lib/integrations/facebook";
import { initIab, uspApi, UspApiFunc } from "../app/core/lib/iab";
import { setupIglooHandler } from "../app/core/lib/igloo";
import { setupLiveRamp } from "../app/core/lib/integrations/live-ramp";
import { getLocale } from "@ihr-radioedit/inferno-core";
import { setupOutbrain } from "../app/core/lib/integrations/outbrain";
import { ParselyManager } from "../app/core/lib/integrations/parsely";
import { isWindowDefined } from "../app/core/lib/window";
import { StartupEventMap } from "@ihr-radioedit/inferno-core";
import { IHRSessionState, SessionBackend } from "@inferno/renderer-shared-core";
import {
  generateDeviceId,
  getDeviceId,
  getUserTestGroups,
  storeDeviceId,
  storeTestGroups,
} from "../app/core/services/ABTest";
import { setupSocialMedia } from "./socialMediaLibs";
import { Store } from "./stores";
import { ILog } from "@ihr-radioedit/inferno-core";

const log = ILog.logger("client/index.tsx");

declare global {
  interface Window {
    __PRELOADED_STATE__: any;
    bootEvents: StartupEventMap;
    moatYieldReady: () => void;
    domCompleteReady: () => void;
    bridgeReady: () => void;
    lazySizesConfig: {
      preloadAfterLoad: boolean;
      init: boolean;
    };
    lazySizes: {
      init: () => void;
      cfg: any;
    };
    recaptchaCallback: (result: string) => void;
    invokeAnalyticsVendorBeacons?: (opts: { ccpa: boolean }) => void;
    deferAnalyticsVendorBeacons?: boolean;
    __uspapi?: UspApiFunc;
    newrelic: typeof import("newrelic");
  }
}

loadableReady(async () => {
  if (typeof performance !== "undefined") {
    performance.mark("Inferno - Loadable Ready");
  }

  // Grab the state from a global variable injected into the server-generated HTML
  const preloadedState = window.__PRELOADED_STATE__;
  // Allow the passed state to be garbage-collected
  delete window.__PRELOADED_STATE__;
  log.debug("Preloaded State: ", preloadedState);

  if (preloadedState.env && (preloadedState.env.LOG_LEVEL || preloadedState.env.DEBUG)) {
    const logLevel = preloadedState.env.DEBUG === "1" ? "debug" : preloadedState.env.LOG_LEVEL?.toLowerCase() || "info";
    log.setLevel(logLevel);
  }

  // Override API_BASE_URI
  preloadedState.api_base_uri = preloadedState.env.API_BASE_URI ? preloadedState.env.API_BASE_URI : "/api/v4";

  // Set up application
  const browserHistory = createBrowserHistory();
  const store = new Store(preloadedState);
  const no3rdParty = store.disableThirdParty;

  store.session.storeSession(getSessionBackend(store) as SessionBackend);
  if (!no3rdParty) {
    store.session.currentSession?.refresh().catch(err => log.error("Session Refresh Error", err));
  }

  const deviceId = getDeviceId() || generateDeviceId();
  if (!no3rdParty) {
    const testGroups = await getUserTestGroups(
      deviceId,
      store.site.index.slug,
      (store.session.currentSession?.state as IHRSessionState).profile,
    );
    store.testGroups = testGroups;
    storeTestGroups(testGroups);
  }
  storeDeviceId(deviceId);

  await i18n(getLocale(store.site).split("-")[0]);

  // const hasQueryParams = Object.keys(store.request.query || {}).length > 0;

  // Hydrate React
  ReactDOM.hydrate(
    <HelmetProvider>
      <Provider store={store}>
        <Router history={browserHistory}>
          <Route component={App} />
        </Router>
      </Provider>
    </HelmetProvider>,
    document.getElementById("app"),
    async () => {
      if (typeof performance !== "undefined") {
        performance.mark("Inferno - React Hydrated");
      }
      try {
        window.lazySizes.init();
      } catch (ignore) {
        // pass
        log.debug("lazysizes not initialized");
      }

      if (no3rdParty) {
        return performance.mark("Inferno - App Loaded - No 3rd Party");
      }
      if (isWindowDefined()) {
        // stubs for googletag
        window.googletag = window.googletag || {};
        window.googletag.cmd = window.googletag.cmd || [];

        const ads = adsHelper(store);
        ads
          ?.init()
          .then(() => {
            log.debug("Ads Helper Ready");
            store.ads = ads;
            ads.scan();
          })
          .catch(err => log.error(`${i18next.t("ads_init")}: ${err}`));
      }

      setBeacon(store);
      new AdobeManager(store);
      new ParselyManager(store);
      new ComScoreManager(store);
      setupOutbrain(store);
      setupLiveRamp(store);
      setupIglooHandler(store);

      try {
        window.__uspapi = uspApi;
      } catch (ignore) {
        log.debug("uspapi not available");
      }
      setupSocialMedia();
      if (typeof performance !== "undefined") {
        performance.mark("Inferno - App Loaded");
      }

      // if (hasQueryParams) {
      //     history.replaceState(null, "", store.request.path);
      // }

      store.session.currentSession
        ?.refresh()
        .catch(e => log.error("Session Refresh Error", e))
        .finally(() => {
          // third parties lib for loggedIn user
          initIab(store);
          initFbSDK(store);
          initFbPixel(store);
        });
      store.session.currentSession?.onStatusChanged.sub(() => {
        initIab(store, true);
      });
    },
  );
});
